import { useState } from 'react';
import { Button, Form, InputGroup, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { v4 as uuidv4 } from 'uuid';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { Api } from 'src/models/Api.model';
import {
  CustomField,
  CustomUserProfileFieldEnum,
  UserPermissionEnum,
} from 'src/models/GalenData/CustomField.model';
import { GalenErrorKeys } from 'src/models/GalenData/GalenError.model';
import { UserStatusEnum } from 'src/models/GalenData/User.model';
import { UserRoleEnum } from 'src/models/GalenData/UserRole.model';
import { I18NextNS } from 'src/models/Language.model';

import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useGetGalenErrorMsg from 'src/hooks/useGetGalenErrorMsg';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useValidatePassword from 'src/hooks/useValidatePassword';

import {
  emailValidators,
  firstNameValidators,
  lastNameValidators,
} from 'src/utils/formValidators';

export type AddUserFormDataTypes = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  password: string;
  confirmPassword: string;
};

interface IAddUserFormModalProps {
  show: boolean;
  handleClose: () => void;
  initializeUsersList: () => void;
}

const AddUserFormModal = ({
  show,
  handleClose,
  initializeUsersList,
}: IAddUserFormModalProps) => {
  const {
    handleSubmit,
    reset,
    register,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      emailAddress: '',
      password: '',
      confirmPassword: '',
    },
    criteriaMode: 'all',
  });

  const handleCloseAndReset = () => {
    handleClose();
    reset();
  };

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.PAGES);

  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);

  const validatePassword = useValidatePassword();

  const { practiceId } = useGetCurrentUser();

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const { getErrorMessage } = useGetGalenErrorMsg();

  const {
    practiceSettings: { hasMfa },
  } = useRemoteBoundStore();

  const handleAddUser = async (data: AddUserFormDataTypes) => {
    try {
      if (!practiceId) {
        throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_IS_REQUIRED));
      }

      const { firstName, lastName, emailAddress, password } = data;

      const res = await Api.remote.getUserCustomFieldDefinitions();

      const customData = [
        {
          field: res.data.content?.find(
            (definition) =>
              definition.name === CustomUserProfileFieldEnum.UserPermission,
          ) as CustomField<CustomUserProfileFieldEnum>,
          fieldData: [UserPermissionEnum.Create],
        },
      ];

      const userId = uuidv4();

      await Api.remote.create_user({
        user: {
          userId,
          firstName,
          lastName,
          emailAddress,
          tenant: { tenantId: import.meta.env.VITE_TENANT_ID },
          roles: [
            {
              defaultRole: true,
              practice: { practiceId },
              role: UserRoleEnum.PracticeUser,
            },
          ],
          status: UserStatusEnum.Active,
          loggableAccount: true,
        },
        password,
        forcePasswordReset: true,
        customData,
      });

      if (hasMfa) {
        await Api.remote.set_user_mfa(true, userId);
      }

      initializeUsersList();
      Api.alertBox({
        title: t('bootbox.title.Success', { ns: I18NextNS.COMMON }),
        message: t('users.addUserSuccessMessage', {
          emailAddress,
          fullName: `${firstName} ${lastName}`,
        }),
        locale: language,
        type: 'success',
      });

      handleCloseAndReset();
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: t('bootbox.title.Error', { ns: I18NextNS.COMMON }),
        message: errorMessage,
        locale: language,
        type: 'error',
      });
    }
  };

  return (
    <CustomModal show={show} onHide={handleCloseAndReset}>
      <Form onSubmit={handleSubmit(handleAddUser)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('users.add')} {t('users.user')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              {t('patientFields.First Name', { ns: I18NextNS.COMMON })}
            </Form.Label>

            <Form.Control
              type="text"
              placeholder={t('patientFields.First Name', {
                ns: I18NextNS.COMMON,
              })}
              data-test="first-name-input"
              {...register('firstName', firstNameValidators(t))}
            />
            {errors.firstName?.message && (
              <p className="text-danger" data-test="first-name-error">
                {errors.firstName?.message}
              </p>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              {t('patientFields.Last Name', { ns: I18NextNS.COMMON })}
            </Form.Label>
            <Form.Control
              placeholder={t('patientFields.Last Name', {
                ns: I18NextNS.COMMON,
              })}
              data-test="last-name-input"
              {...register('lastName', lastNameValidators(t))}
            />
            {errors.lastName?.message && (
              <p className="text-danger" data-test="last-name-error">
                {errors.lastName?.message}
              </p>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              {t('patientFields.email', { ns: I18NextNS.COMMON })}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder={t('patientFields.emailPlaceholder', {
                ns: I18NextNS.COMMON,
              })}
              data-test="email-input"
              autoComplete="off"
              {...register('emailAddress', emailValidators(t))}
            ></Form.Control>
            {errors.emailAddress?.message && (
              <p className="text-danger" data-test="email-error">
                {errors.emailAddress?.message}
              </p>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              {t('patientFields.password', { ns: I18NextNS.COMMON })}
            </Form.Label>
            <InputGroup>
              <Form.Control
                type={isShowPassword ? 'text' : 'password'}
                placeholder={t('patientFields.passwordPlaceholder', {
                  ns: I18NextNS.COMMON,
                })}
                data-test="password-input"
                {...register('password', {
                  required: t('patientFields.passwordRequired', {
                    ns: I18NextNS.COMMON,
                  }),
                  validate: validatePassword,
                })}
              />
              <InputGroup.Text
                onClick={() => setIsShowPassword(!isShowPassword)}
                className="pointer"
                data-test="password-visibility-toggle"
              >
                <FontAwesomeIcon icon={isShowPassword ? faEye : faEyeSlash} />
              </InputGroup.Text>
            </InputGroup>
            <div className="text-danger" data-test="password-error">
              {errors.password?.type === 'required' ? (
                <p>
                  {t('patientFields.passwordRequired', {
                    ns: I18NextNS.COMMON,
                  })}
                </p>
              ) : (
                <ul className="list-unstyled">
                  {errors.password?.types?.minLength && (
                    <li>{errors.password.message}</li>
                  )}
                  {errors.password?.types?.hasUpperCase && (
                    <li>{errors.password.types.hasUpperCase}</li>
                  )}
                  {errors.password?.types?.hasLowerCase && (
                    <li>{errors.password.types.hasLowerCase}</li>
                  )}
                  {errors.password?.types?.hasNumber && (
                    <li>{errors.password.types.hasNumber}</li>
                  )}
                  {errors.password?.types?.hasSpecialChar && (
                    <li>{errors.password.types.hasSpecialChar}</li>
                  )}
                </ul>
              )}
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              {t('patientFields.confirmPassword', { ns: I18NextNS.COMMON })}
            </Form.Label>
            <InputGroup>
              <Form.Control
                type={isShowConfirmPassword ? 'text' : 'password'}
                placeholder={t('patientFields.confirmPasswordPlaceholder', {
                  ns: I18NextNS.COMMON,
                })}
                data-test="confirm-password-input"
                {...register('confirmPassword', {
                  required: t('patientFields.confirmPasswordRequired', {
                    ns: I18NextNS.COMMON,
                  }),
                  validate: (val: string) => {
                    if (watch('password') !== val) {
                      return t('patientFields.passwordsDoNotMatch', {
                        ns: I18NextNS.COMMON,
                      });
                    }
                  },
                })}
              />
              <InputGroup.Text
                onClick={() => setIsShowConfirmPassword(!isShowConfirmPassword)}
                className="pointer"
                data-test="confirm-password-visibility-toggle"
              >
                <FontAwesomeIcon
                  icon={isShowConfirmPassword ? faEye : faEyeSlash}
                />
              </InputGroup.Text>
            </InputGroup>
            {
              <p className="text-danger" data-test="confirm-password-error">
                {errors.confirmPassword?.message}
              </p>
            }
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="primary-100" onClick={handleCloseAndReset}>
            {t('users.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            data-test="submit-member-form"
            disabled={isSubmitting || !isDirty}
          >
            {isSubmitting ? t('users.saving') : t('users.save')}
          </Button>
        </Modal.Footer>
      </Form>
    </CustomModal>
  );
};

export default AddUserFormModal;
